import { LabelHTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { Text } from '../../../Text';
import { cover } from '~/shared/utils/styled';

const heightToken = 5;
const eagerWidthToken = 2;

export const StyledToggleSwitchContainer = styled.fieldset<{
    hasGap?: boolean;
}>(({ theme, hasGap }) => ({
    display: 'flex',
    gap: hasGap ? theme.spaces[3] : undefined,
    flexWrap: 'nowrap',
}));

export const StyledToggleSwitchWrapper = styled.span(
    ({ theme: { spaces, animations, colors } }) => ({
        position: 'relative',
        flexShrink: 0,
        marginTop: spaces[1],
        width: `calc(${spaces[heightToken]} * 2)`,
        height: spaces[heightToken],
        borderRadius: spaces[heightToken],
        background: colors.light40,
        transitionDuration: animations.getDuration('fast02'),
        transitionTimingFunction: animations.getEasing('standard', 'productive'),

        '&:before': {
            content: '""',
            ...cover,
            borderRadius: 'inherit',
            boxShadow: `0 0 0 1px ${colors.dark} inset`,
            zIndex: 3,
            pointerEvents: 'none',
        },
        '*:disabled &': {
            backgroundColor: colors.light30,
        },
        '*:disabled &:before': {
            boxShadow: `0 0 0 1px ${colors.light50} inset`,
        },
    })
);

export const StyledToggleSwitchInput = styled.input({
    width: '100%',
    height: '100%',
    margin: 0,
    opacity: 0,
    borderRadius: 'inherit',
    cursor: 'pointer',
    '&:disabled': {
        pointerEvents: 'none',
    },
});

export const StyledToggleSwitchOutline = styled.span(({ theme: { focus } }) => ({
    ...cover,
    borderRadius: 'inherit',
    outlineOffset: 2,
    pointerEvents: 'none',
    'input:focus-visible ~ &': {
        outline: `1px solid ${focus.outlineColor}`,
    },
}));

export const StyledToggleSwitch = styled.span(({ theme: { spaces, colors } }) => ({
    display: 'inline-block',
    width: '100%',
    height: '100%',
    ...cover,
    borderRadius: 'inherit',
    transitionDuration: 'inherit',
    transitionTimingFunction: 'inherit',
    overflow: 'hidden',
    pointerEvents: 'none', // Click through, to the input element below

    ['&:before, &:after']: {
        content: '""',
        ...cover,
        cursor: 'pointer',
        height: '100%',
        transitionProperty: 'transform, width', // Transform on width, but isolated to only this pseudoelement, so performance is fine.
        transitionDuration: 'inherit',
        transitionTimingFunction: 'inherit',
    },

    '&:before': {
        width: '75%',
        background: colors.dark,
        transform: 'translateX(-50%)',
    },

    '&:after': {
        width: '50%',
        right: 'auto',
        zIndex: 2,
        boxShadow: `0 0 0 1px ${colors.dark} inset`,
        background: colors.light,
        borderRadius: 'inherit',
    },

    'input:checked ~ &': {
        '&:before': {
            transform: 'translateX(0)',
        },
        '&:after': {
            transform: 'translateX(100%)',
        },
    },

    'input:active ~ &:after': {
        width: `calc(50% + ${spaces[eagerWidthToken]})`,
    },

    'input:checked:active ~ &:after': {
        // "1.75" for a a little less eagerness to be unchecked,
        // and also handles slight jitter that would occur if this was "2" as it should have been
        transform: `translateX(calc(100% - (${spaces[eagerWidthToken]} * 1.75)))`,
    },

    '*:disabled &:before, *:disabled &:after': {
        boxShadow: `0 0 0 1px ${colors.light50} inset`,
    },
}));

export const StyledLabel = styled(Text)<LabelHTMLAttributes<HTMLLabelElement>>({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
});
